import { ReactNode } from 'react';

import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useFieldContext } from '@/forms/formContext';
import { BaseInputFieldProps } from '@/forms/types';

type CheckboxFieldProps = Omit<
  BaseInputFieldProps,
  'placeholder' | 'showIsValid'
> &
  Omit<CheckboxProps, 'checked' | 'onChange' | 'name' | 'id'> & {
    helperText?: string | ReactNode;
  };

export const CheckboxField = ({
  required,
  label,
  helperText,
  ...props
}: CheckboxFieldProps) => {
  const field = useFieldContext();

  const isError = !!(
    (!field.state.meta.isValid && field.state.meta.isDirty) ||
    (!field.state.meta.isValid && field.form.state.submissionAttempts > 0)
  );

  const { t } = useTranslation('forms');

  const helperTextId =
    helperText && !isError ? `id_${field.name}_helper` : undefined;
  const displayError = isError ? field.state.meta.errors[0] : null;
  const displayErrorId = displayError ? `id_${field.name}_error` : null;
  const describedByIds =
    [helperTextId, displayErrorId].filter(Boolean).join(' ') || undefined;

  return (
    <FormControl error={isError} fullWidth={false}>
      <FormControlLabel
        label={label}
        required={required}
        control={
          <Checkbox
            {...props}
            id={field.name}
            name={field.name}
            checked={!!field.state.value}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.checked)}
            aria-describedby={describedByIds}
          />
        }
      />
      {isError && displayError && displayErrorId ? (
        <FormHelperText error id={displayErrorId}>
          {displayError.message?.startsWith('validationErrors.')
            ? t(displayError.message, {
                value: label ?? t('validationErrors.DefaultValueLabel'),
              })
            : displayError.message}
        </FormHelperText>
      ) : null}
      {helperText && !isError && (
        <FormHelperText
          id={helperTextId}
          component={typeof helperText === 'string' ? 'p' : 'div'}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
